// src/pages/invoices/pdfgenerator.tsx
import { ASSET_BASE_URL } from '@/config';
import { getBankOptionLabel } from '@/lib/constants/bank-details';
import { getStatesLabel } from '@/lib/constants/common';
import {
  currencySymbols,
  getInvoiceTitleLabel,
  gstStateList,
  TAX_META,
} from '@/lib/constants/invoice';
import {
  calculateTaxBreakdown,
  formatNumberWithCommas,
  getGstType,
  numberToWords,
  parseTaxValue,
} from '@/lib/utils/invoice';
import { InvoicePDFData } from '@/types/invoice.types';
import { Document, Font, Image, Page, pdf, Text, View, Link } from '@react-pdf/renderer';
import { styles } from './pdfgenerator.styles';

// Register Noto Sans font for better Unicode support
Font.register({
  family: 'Noto Sans',
  fonts: [
    { src: '/fonts/NotoSans-Regular.ttf', fontWeight: 400 },
    { src: '/fonts/NotoSans-Medium.ttf', fontWeight: 500 },
    { src: '/fonts/NotoSans-SemiBold.ttf', fontWeight: 600 },
    { src: '/fonts/NotoSans-Bold.ttf', fontWeight: 700 },
    { src: '/fonts/NotoSans-Italic.ttf', fontStyle: 'italic', fontWeight: 400 },
    // {
    //   src: 'https://fonts.gstatic.com/s/notosans/v36/o-0mIpQlx3QUlC5A4PNB6Ryti20_6n1iPHjcz6L1SoM-jCpoiyD9A-9a6Vc.ttf',
    //   fontStyle: 'italic',
    //   fontWeight: 700
    // },
  ],
});

const formatDate = (dateString: string) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const getCurrencySymbol = (currency: string) => {
  return currencySymbols[currency as keyof typeof currencySymbols] || currency;
};

const getNormalizedInvoiceTitleType = (data: any): string => {
  const rawTitleType =
    data?.invoiceTitleType ??
    data?.InvoiceTitleType ??
    data?.invoiceTitle ??
    data?.invoiceTitleLabel ??
    'tax_invoice';
  const normalized = String(rawTitleType).trim().toLowerCase();

  if (normalized.includes('proforma')) return 'proforma_invoice';
  if (normalized.includes('overdue')) return 'overdue_invoice';
  if (normalized.includes('consolidated')) return 'consolidated_invoice';
  if (normalized.includes('retainer')) return 'retainer_invoice';
  if (normalized.includes('interim')) return 'interim_invoice';
  if (normalized.includes('timesheet')) return 'timesheet_invoice';
  if (normalized.includes('final')) return 'final_invoice';
  return 'tax_invoice';
};

const InvoicePDF = ({ data }: { data: InvoicePDFData }) => {
  const { items, currency } = data;
  const currencyDisplay = getCurrencySymbol(currency);

  // Calculate totals
  const taxBreakdown = calculateTaxBreakdown(items, data.gstType);
  const hasGST = taxBreakdown.length > 0;
  const round2 = (n: number) => Math.round(n * 100) / 100;

  const subTotal = round2(items.reduce((sum, item) => sum + item.rate * item.quantity, 0));
  const totalTax = round2(taxBreakdown.reduce((sum, line) => sum + line.amount, 0));
  const grandTotal = round2(subTotal + totalTax);

  const taxTypesUsed = new Set(items.map((i) => i.taxValue || i.taxRate));

  let taxNote = null;
  if (taxTypesUsed.has('out_of_scope')) {
    taxNote = TAX_META.out_of_scope.note;
  } else if (taxTypesUsed.has('non_gst_supply')) {
    taxNote = TAX_META.non_gst_supply.note;
  }

  // Amount in words
  const amountInWords = numberToWords(grandTotal, currency);

  // Check if bank details are available
  const hasBankDetails = Boolean(
    data.bankName || data.bankAccountName || data.accountNumber || data.ifscCode
  );

  // Determine if we have company data or should show individual name
  const hasCompanyData = Boolean(data.companyName && data.invoiceType === 'company');
  const isIndividual = data.invoiceType === 'individual' || !hasCompanyData;

  // Get the seller/business name to display
  const getSellerName = (): string => {
    if (hasCompanyData && data.companyName) {
      return data.companyName.toUpperCase();
    }
    if (data.individualName) {
      return data.individualName.toUpperCase();
    }
    return '';
  };

  const sellerName = getSellerName();
  const companyGstin = (data as any).companyGstin || '';
  const customerGstin = (data as any).customerGstin || '';

  const normalizedInvoiceTitleType = getNormalizedInvoiceTitleType(data);
  const isProformaInvoice = normalizedInvoiceTitleType === 'proforma_invoice';
  const invoiceTitle = getInvoiceTitleLabel(normalizedInvoiceTitleType);
  const invoiceDateLabel = isProformaInvoice ? 'Proforma Date:' : 'Invoice Date:';
  const balanceDueLabel = isProformaInvoice ? 'Estimated Amount' : 'Balance Due';
  let invoiceTitleStr = invoiceTitle;
  if (!hasCompanyData && sellerName) {
    invoiceTitleStr = `${sellerName} ${invoiceTitle}`;
  }

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header Section */}
        <View style={[styles.header, !hasCompanyData ? styles.headerNoCompany : {}]}>
          {hasCompanyData && (
            <View style={styles.leftHeader}>
              {data.logoUrl && <Image src={data.logoUrl} style={styles.logo} />}
              {sellerName && <Text style={styles.companyName}>{sellerName}</Text>}
              {data.companyAddress && (
                <Text style={styles.companyAddress}>{data.companyAddress}</Text>
              )}
              {companyGstin && <Text style={styles.companyAddress}>GSTIN {companyGstin}</Text>}
            </View>
          )}

          <View style={[styles.rightHeader, !hasCompanyData ? { width: '100%' } : {}]}>
            <Text style={styles.invoiceTitle}>{invoiceTitleStr}</Text>

            <Text style={styles.invoiceNumber}>#{data.invoiceNumber}</Text>

            <View style={styles.balanceDueContainer}>
              <Text style={styles.balanceDueLabel}>{balanceDueLabel}</Text>
              <Text style={styles.balanceDueAmount}>
                {currencyDisplay}
                {formatNumberWithCommas(grandTotal.toFixed(2))}
              </Text>
            </View>
          </View>
        </View>

        {/* Details Section */}
        <View style={styles.detailsSection}>
          {/* Bill To */}
          <View style={styles.billToSection}>
            <Text style={styles.sectionTitle}>Bill To</Text>
            <Text style={styles.customerName}>
              {data.customerDetails?.customerType === 'business'
                ? data.customerDetails?.companyName?.toUpperCase()
                : data.customerDetails?.primaryContact?.toUpperCase() || 'CUSTOMER'}
            </Text>
            {data.customerDetails?.addressLines?.map((line, idx) => (
              <Text key={idx} style={styles.addressLine}>
                {line}
              </Text>
            ))}
            {customerGstin && <Text style={styles.addressLine}>GSTIN {customerGstin}</Text>}
            {data.placeOfSupply && (
              <View style={styles.placeOfSupplyRow}>
                <Text style={styles.placeOfSupplyLabel}>Place of Supply:</Text>
                <Text style={styles.placeOfSupplyValue}>{data.placeOfSupply}</Text>
              </View>
            )}
          </View>

          {/* Invoice Info */}
          <View style={styles.invoiceInfoSection}>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>{invoiceDateLabel}</Text>
              <Text style={styles.infoValue}>
                {data.invoiceDate ? formatDate(data.invoiceDate) : 'N/A'}
              </Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Payment Terms:</Text>
              <Text style={styles.infoValue}>{data.terms}</Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Due Date:</Text>
              <Text style={styles.infoValue}>
                {data.dueDate ? formatDate(data.dueDate) : 'N/A'}
              </Text>
            </View>
          </View>
        </View>

        {/* Items Table */}
        <View style={styles.table}>
          <View style={styles.tableHeader} fixed>
            <Text style={styles.colIndex}>Sr No.</Text>
            <Text style={styles.colDescription}>Description</Text>
            <Text style={styles.colHsn}>HSN/SAC</Text>
            <Text style={styles.colQuantity}>Qty</Text>
            <Text style={styles.colRate}>Rate</Text>
            <Text style={styles.colTaxRate}>Tax %</Text>
            <Text style={styles.colAmount}>Amount</Text>
          </View>

          {items.map((item, index) => {
            const baseAmount = item.rate * item.quantity;
            const taxAmount = (baseAmount * (item.taxRate || 0)) / 100;
            const totalAmount = baseAmount + taxAmount;

            return (
              <View
                key={index}
                style={[styles.tableRow, index % 2 === 1 ? styles.tableRowAlt : {}]}
                wrap={false}
              >
                <Text style={styles.colIndex}>{index + 1}</Text>

                <View style={styles.colDescription}>
                  <Text style={styles.itemName}>{item.name}</Text>
                  {item.description && (
                    <Text style={styles.descriptionDetail}>{item.description}</Text>
                  )}
                </View>

                <Text style={styles.colHsn}>
                  {item.hsnSac || item.itemId?.hsnCode || item.itemId?.sacCode || '-'}
                </Text>

                <Text style={styles.colQuantity}>{item.quantity.toFixed(2)}</Text>

                <Text style={styles.colRate}>{formatNumberWithCommas(item.rate.toFixed(2))}</Text>

                {/* TAX % COLUMN */}
                <View style={styles.colTaxRate}>
                  {Number(item.taxRate) === 0 ? (
                    <Text style={{ fontSize: 8, color: '#666666' }}>
                      {item.taxValue === 'non_gst_supply'
                        ? 'Non-GST Supply'
                        : item.taxValue === 'out_of_scope'
                          ? 'Out of Scope'
                          : '-'}
                    </Text>
                  ) : (
                    <>
                      <Text>{item.taxRate}%</Text>
                      {parseTaxValue(item.taxValue, data.gstType).gstType === 'IGST' ? (
                        <Text style={styles.taxSplit}>IGST</Text>
                      ) : (
                        <>
                          <Text style={styles.taxSplit}>CGST {Number(item.taxRate) / 2}%</Text>
                          <Text style={styles.taxSplit}>SGST {Number(item.taxRate) / 2}%</Text>
                        </>
                      )}
                    </>
                  )}
                </View>

                {/* AMOUNT COLUMN (MATCHES IGST SUMMARY) */}
                <View style={styles.colAmount}>
                  {/* Total incl. tax */}
                  <Text>{formatNumberWithCommas(totalAmount.toFixed(2))}</Text>

                  {/* EXACT IGST VALUE (same as summary) */}
                </View>
              </View>
            );
          })}
        </View>

        {/* Summary Section */}
        <View style={styles.summarySection} wrap={false}>
          {/* LEFT — Payment Information */}
          {hasBankDetails ? (
            <View style={styles.bankDetails}>
              <Text style={styles.bankTitle}>Bank & Payment Details</Text>

              <View style={styles.bankInfoRow}>
                <Text style={styles.bankLabel}>Bank Name:</Text>
                <Text style={styles.bankValue}>{data.bankName}</Text>
              </View>

              <View style={styles.bankInfoRow}>
                <Text style={styles.bankLabel}>Account Name:</Text>
                <Text style={styles.bankValue}>{data.bankAccountName}</Text>
              </View>

              <View style={styles.bankInfoRow}>
                <Text style={styles.bankLabel}>Account Number:</Text>
                <Text style={styles.bankValue}>{data.accountNumber}</Text>
              </View>

              <View style={styles.bankInfoRow}>
                <Text style={styles.bankLabel}>IFSC Code:</Text>
                <Text style={styles.bankValue}>{data.ifscCode}</Text>
              </View>

              {data.upi && (
                <View style={styles.bankInfoRow}>
                  <Text style={styles.bankLabel}>UPI ID:</Text>
                  <Text style={styles.bankValue}>{data.upi}</Text>
                </View>
              )}
            </View>
          ) : (
            // Empty placeholder to maintain layout when no bank details
            <View style={styles.bankDetails} />
          )}

          {/* RIGHT — Totals */}
          <View style={styles.totalsSection}>
            <View style={styles.totalsContainer} wrap={false}>
              <View style={styles.totalRow}>
                <Text style={styles.totalLabel}>Subtotal</Text>
                <Text style={styles.totalValue}>
                  {currencyDisplay}
                  {formatNumberWithCommas(subTotal.toFixed(2))}
                </Text>
              </View>

              {hasGST &&
                taxBreakdown.map((line) => (
                  <View key={`${line.label}-${line.rate}`} style={styles.totalRow}>
                    <Text style={styles.totalLabel}>
                      {line.label} ({line.rate}%)
                    </Text>
                    <Text style={styles.totalValue}>
                      {currencyDisplay}
                      {formatNumberWithCommas(line.amount.toFixed(2))}
                    </Text>
                  </View>
                ))}

              {hasGST && totalTax > 0 && (
                <View style={styles.totalRow}>
                  <Text style={styles.totalLabel}>Total Tax</Text>
                  <Text style={styles.totalValue}>
                    {currencyDisplay}
                    {formatNumberWithCommas(totalTax.toFixed(2))}
                  </Text>
                </View>
              )}

              {taxNote && <Text style={styles.taxNote}>{taxNote}</Text>}

              <View style={styles.grandTotalRow}>
                <Text style={styles.grandTotalLabel}>{balanceDueLabel}</Text>
                <Text style={styles.grandTotalValue}>
                  {currencyDisplay}
                  {formatNumberWithCommas(grandTotal.toFixed(2))}
                </Text>
              </View>

              <Text style={styles.amountInWords}>
                Total In Words: <Text style={styles.amountInWordsValue}>{amountInWords}</Text>
              </Text>
            </View>
          </View>
        </View>

        {/* Footer */}
        {/* <Text style={styles.footer}>
          This is a computer-generated invoice and does not require a signature.
        </Text> */}
        <View style={styles.footer}>
          <Text>This is a computer-generated invoice and does not require a signature.</Text>

          {/* <Text>Powered by
            <Link src="https://trakshym.com" style={styles.poweredBy}>
              Trakshym
            </Link>
          </Text> */}

          {data.branding !== true && (
            <Text>
              Powered by
              <Text> </Text>
              <Link src="https://trakshym.com" style={styles.poweredBy}>
                Trakshym
              </Link>
            </Text>
          )}
        </View>
      </Page>
    </Document>
  );
};

export const generatePDF = async (
  formData: any,
  items: any[],
  selectedCompany: any,
  selectedCustomer: any,
  selectedBank: any,
  options: { download?: boolean } = {}
) => {
  const normalizeState = (str: string) =>
    str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : '';

  const rawState = getStatesLabel(formData.placeOfSupply) || formData.placeOfSupply;
  const normalizedState = normalizeState(rawState?.trim());

  const stateInfo = normalizedState && gstStateList[normalizedState as keyof typeof gstStateList];

  const formattedPlaceOfSupply: string = stateInfo
    ? `[${stateInfo.abbreviation}] - ${String(rawState)} (${stateInfo.code})`
    : String(rawState || '');

  const hasCompanyData = Boolean(selectedCompany?.companyName);
  const invoiceType = hasCompanyData ? 'company' : 'individual';

  const individualName = formData.individualName || formData.companyId?.individualName || '';

  const localBranding = localStorage.getItem(`branding_${formData.invoiceNumber || 'INV-001'}`);
  const isBrandingEnabled = localBranding ? localBranding === 'true' : (formData.branding ?? true);

  const data: InvoicePDFData = {
    invoiceNumber: formData.invoiceNumber || 'INV-001',
    // Set invoice type based on whether company data is available
    invoiceType: invoiceType,
    invoiceTitleType: getNormalizedInvoiceTitleType(formData),
    individualName: individualName,

    customerDetails: {
      companyName: selectedCustomer?.companyName || '',
      primaryContact: selectedCustomer?.primaryContact || '',
      customerType: selectedCustomer?.customerType || '',
      addressLines: [
        selectedCustomer?.customerAddress?.address || '',
        [selectedCustomer?.customerAddress?.city, selectedCustomer?.customerAddress?.state]
          .filter(Boolean)
          .join(', '),
        selectedCustomer?.customerAddress?.pinCode || '',
        selectedCustomer?.customerAddress?.countryRegion || '',
      ].filter(Boolean),
    },

    placeOfSupply: formattedPlaceOfSupply,
    invoiceDate: formData.invoiceDate || new Date().toISOString(),
    dueDate: formData.dueDate || formData.invoiceDate || new Date().toISOString(),
    terms: formData.terms && formData.terms.trim() !== '' ? formData.terms : 'Due on Receipt',

    // Set company name - use individual name as fallback
    companyName: hasCompanyData ? selectedCompany.companyName : individualName || '',

    companyAddress: selectedCompany?.companyAddress
      ? [
          selectedCompany.companyAddress.addressLine1,
          selectedCompany.companyAddress.addressLine2,
          selectedCompany.companyAddress.city,
          selectedCompany.companyAddress.state,
          selectedCompany.companyAddress.pinCode,
        ]
          .filter(Boolean)
          .join(', ')
      : '',

    // Only use logo if company data is available
    logoUrl:
      hasCompanyData && selectedCompany?.companyLogo
        ? selectedCompany.companyLogo.startsWith('data:')
          ? selectedCompany.companyLogo
          : `${ASSET_BASE_URL}${selectedCompany.companyLogo}`
        : '',

    currency: formData.customerId?.currency || formData.currency || 'INR',
    gstType: getGstType(selectedCompany?.placeOfSupply, formData.placeOfSupply),

    // Handle missing bank details gracefully
    bankName: selectedBank?.bankName ? getBankOptionLabel(selectedBank.bankName) : '',
    bankAccountName: selectedBank?.accountHolderName || '',
    accountNumber: selectedBank?.accountNumber || '',
    ifscCode: selectedBank?.ifscCode || '',
    upi: selectedBank?.upiId || '',

    items: items.map((item) => ({
      name: item.itemId?.itemName || item.itemName || '',
      description: item.itemId?.description || item.description || '',
      hsnSac: item.itemId?.sacCode || item.itemId?.hsnCode || item.sacCode || item.hsnCode || '',
      quantity: parseFloat(item.quantity) || 0,
      rate: parseFloat(item.rate) || 0,
      amount: parseFloat(item.amount) || 0,
      taxRate: parseFloat(item.taxRate) || 0,
      taxValue: item.taxValue || '',
    })),
    branding: isBrandingEnabled,
  };
  (data as any).companyGstin = selectedCompany?.gstTreatment?.gstNumber || '';
  (data as any).customerGstin = selectedCustomer?.gstTreatment?.gstNumber || '';

  try {
    const blob = await pdf(<InvoicePDF data={data} />).toBlob();
    const url = URL.createObjectURL(blob);
    if (options.download) {
      // Download the PDF
      const link = document.createElement('a');
      link.href = url;
      link.download = `${formData.invoiceNumber || 'INV'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      // Preview in new tab
      window.open(url, '_blank');
    }

    // Clean up URL after a delay
    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 60000); // Revoke after 1 minute
  } catch (error) {
    console.error('PDF generation failed:', error);
    alert('Failed to generate PDF. Please try again.');
  }
};

export const generatePDFBlob = async (
  formData: any,
  items: any[],
  selectedCompany: any,
  selectedCustomer: any,
  selectedBank: any,
  options: { download?: boolean } = {}
) => {
  const normalizeState = (str: string) =>
    str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : '';

  const rawState = getStatesLabel(formData.placeOfSupply) || formData.placeOfSupply;
  const normalizedState = normalizeState(rawState?.trim());

  const stateInfo = normalizedState && gstStateList[normalizedState as keyof typeof gstStateList];

  const formattedPlaceOfSupply: string = stateInfo
    ? `[${stateInfo.abbreviation}] - ${String(rawState)} (${stateInfo.code})`
    : String(rawState || '');

  const hasCompanyData = Boolean(selectedCompany?.companyName);
  const invoiceType = hasCompanyData ? 'company' : 'individual';

  const individualName = formData.individualName || formData.companyId?.individualName || '';

  const localBranding = localStorage.getItem(`branding_${formData.invoiceNumber || 'INV-001'}`);
  const isBrandingEnabled = localBranding ? localBranding === 'true' : (formData.branding ?? true);

  const data: InvoicePDFData = {
    invoiceNumber: formData.invoiceNumber || 'INV-001',
    // Set invoice type based on whether company data is available
    invoiceType: invoiceType,
    invoiceTitleType: getNormalizedInvoiceTitleType(formData),
    individualName: individualName,

    customerDetails: {
      companyName: selectedCustomer?.companyName || '',
      primaryContact: selectedCustomer?.primaryContact || '',
      customerType: selectedCustomer?.customerType || '',
      addressLines: [
        selectedCustomer?.customerAddress?.address || '',
        [selectedCustomer?.customerAddress?.city, selectedCustomer?.customerAddress?.state]
          .filter(Boolean)
          .join(', '),
        selectedCustomer?.customerAddress?.pinCode || '',
        selectedCustomer?.customerAddress?.countryRegion || '',
      ].filter(Boolean),
    },

    placeOfSupply: formattedPlaceOfSupply,
    invoiceDate: formData.invoiceDate || new Date().toISOString(),
    dueDate: formData.dueDate || formData.invoiceDate || new Date().toISOString(),
    terms: formData.terms && formData.terms.trim() !== '' ? formData.terms : 'Due on Receipt',

    // Set company name - use individual name as fallback
    companyName: hasCompanyData ? selectedCompany.companyName : individualName || '',

    companyAddress: selectedCompany?.companyAddress
      ? [
          selectedCompany.companyAddress.addressLine1,
          selectedCompany.companyAddress.addressLine2,
          selectedCompany.companyAddress.city,
          selectedCompany.companyAddress.state,
          selectedCompany.companyAddress.pinCode,
        ]
          .filter(Boolean)
          .join(', ')
      : '',

    // Only use logo if company data is available
    logoUrl:
      hasCompanyData && selectedCompany?.companyLogo
        ? selectedCompany.companyLogo.startsWith('data:')
          ? selectedCompany.companyLogo
          : `${ASSET_BASE_URL}${selectedCompany.companyLogo}`
        : '',

    currency: formData.customerId?.currency || formData.currency || 'INR',
    gstType: getGstType(selectedCompany?.placeOfSupply, formData.placeOfSupply),

    // Handle missing bank details gracefully
    bankName: selectedBank?.bankName ? getBankOptionLabel(selectedBank.bankName) : '',
    bankAccountName: selectedBank?.accountHolderName || '',
    accountNumber: selectedBank?.accountNumber || '',
    ifscCode: selectedBank?.ifscCode || '',
    upi: selectedBank?.upiId || '',

    items: items.map((item) => ({
      name: item.itemId?.itemName || item.itemName || '',
      description: item.itemId?.description || item.description || '',
      hsnSac: item.itemId?.sacCode || item.itemId?.hsnCode || item.sacCode || item.hsnCode || '',
      quantity: parseFloat(item.quantity) || 0,
      rate: parseFloat(item.rate) || 0,
      amount: parseFloat(item.amount) || 0,
      taxRate: parseFloat(item.taxRate) || 0,
      taxValue: item.taxValue || '',
    })),
    branding: isBrandingEnabled,
  };
  (data as any).companyGstin = selectedCompany?.gstTreatment?.gstNumber || '';
  (data as any).customerGstin = selectedCustomer?.gstTreatment?.gstNumber || '';
  const fileName = `${formData.invoiceNumber || 'INV'}.pdf`;

  try {
    const blob = await pdf(<InvoicePDF data={data} />).toBlob();
    return { blob, fileName };
  } catch (error) {
    console.error('PDF generation failed:', error);
    return null;
  }
};
